import { useCallback, useEffect, useRef, useState } from 'react';

var isElementVisible = function isElementVisible(el) {
  return el && !!el.offsetParent;
};

var elementsNotVisible = function elementsNotVisible(linksDiv, actionsDiv) {
  return !isElementVisible(linksDiv) && !isElementVisible(actionsDiv);
};

var noMoreLinksToOverflow = function noMoreLinksToOverflow(overflowLinks, totalLinks) {
  return overflowLinks >= totalLinks;
};

export var useFitLinksInNav = function useFitLinksInNav(totalLinks) {
  // Start with ALL items visible to allow proper measurement
  var _useState = useState(0),
      overflowLinks = _useState[0],
      setOverflowLinks = _useState[1];

  var overflowLinksRef = useRef(0);
  var toolbarRef = useRef(null);
  var actionsRef = useRef(null);
  var spaceRef = useRef(null);
  var linksRef = useRef(null); // Store item widths measured on first render (before any are hidden)

  var itemWidthsRef = useRef([]);
  var initialMeasurementDoneRef = useRef(false);
  var handleResize = useCallback(function () {
    var _spaceRef$current, _linksRef$current, _actionsRef$current;

    if (elementsNotVisible(linksRef.current, actionsRef.current) || noMoreLinksToOverflow(overflowLinksRef.current, totalLinks)) return;
    var spaceAvailable = ((_spaceRef$current = spaceRef.current) === null || _spaceRef$current === void 0 ? void 0 : _spaceRef$current.getBoundingClientRect().width) || 0;
    var linksWidth = ((_linksRef$current = linksRef.current) === null || _linksRef$current === void 0 ? void 0 : _linksRef$current.getBoundingClientRect().width) || 0;
    var actionsWidth = ((_actionsRef$current = actionsRef.current) === null || _actionsRef$current === void 0 ? void 0 : _actionsRef$current.getBoundingClientRect().width) || 0; // Magic number 48: padding/margin buffer for nav elements

    var totalWidth = linksWidth + spaceAvailable + actionsWidth + 48; // Store previous state to detect actual changes

    var previousOverflow = overflowLinksRef.current; // On first call, measure ALL nav items and store their widths
    // This is why we start with overflowLinks=0 (all visible)

    if (!initialMeasurementDoneRef.current && linksRef.current) {
      var allLinks = linksRef.current.querySelectorAll('a');
      itemWidthsRef.current = Array.from(allLinks).map(function (link) {
        return link.getBoundingClientRect().width;
      });
      initialMeasurementDoneRef.current = true;
    } // Hide items immediately when they overflow the window


    if (Math.floor(totalWidth) > window.innerWidth) {
      overflowLinksRef.current++;
    } // Show items only when there's sufficient space
    else if (overflowLinksRef.current > 0 && spaceAvailable > 0) {
      // Calculate index of next item to show (first hidden item from left)
      var nextItemIndex = Math.min(totalLinks - overflowLinksRef.current + 1, totalLinks - 1);
      var nextItemWidth = itemWidthsRef.current[nextItemIndex] || 0;
      var dynamicThreshold = nextItemWidth > 0 ? nextItemWidth : 150;

      if (spaceAvailable > dynamicThreshold) {
        overflowLinksRef.current--;
      }
    } // Only trigger re-render if overflow count actually changed


    if (overflowLinksRef.current !== previousOverflow) {
      setOverflowLinks(overflowLinksRef.current);
    }
  }, [actionsRef, totalLinks, overflowLinksRef, linksRef, spaceRef]);
  useEffect(function () {
    var resizeObserver = new ResizeObserver(handleResize);
    if (toolbarRef.current) resizeObserver.observe(toolbarRef.current);
    if (linksRef.current) resizeObserver.observe(linksRef.current);
    if (actionsRef.current) resizeObserver.observe(actionsRef.current);
    return function () {
      return resizeObserver.disconnect();
    };
  }, [toolbarRef.current, linksRef.current, actionsRef.current]);
  return {
    toolbarRef: toolbarRef,
    actionsRef: actionsRef,
    spaceRef: spaceRef,
    linksRef: linksRef,
    overflowLinks: overflowLinks
  };
};